import {
  iconListEnd,
  iconRedo2,
  iconRefreshCcw,
  iconUndo2,
} from "@kensho/icons";
import { Button, Icon, Tooltip } from "@kensho/neo";
import clsx from "clsx";
import { use } from "react";

import { Skeleton } from "../../../components/skeleton";
import { UpdateTranscriptSelectionType } from "../../../hooks/use-transcript-selection";
import {
  APITranscript,
  AsyncStatus,
  Stage,
  TranscriptMetadata,
  TranscriptSelection,
} from "../../../types/types";
import { TranscriptEditAction } from "../../../utils/transcript-revision-utils";
import { AlignSelectedButton } from "../actions/align-selected-button";
import { CopyToClipboardButton } from "../actions/copy-to-clipboard-button";
import { CreateNeedsReviewHighlightButton } from "../actions/create-needs-review-highlight-button";
import { FootPedalButton } from "../actions/foot-pedal-button";
import { ImportTranscriptButton } from "../actions/import-transcript-button";
import { ReviewButton } from "../actions/review-button";
import { LastSavedStatus } from "../last-saved-status";
import { TranscriptPermissionsContext } from "../transcript-permissions-provider";

interface ToolbarProps {
  metadata?: TranscriptMetadata;
  stage: Stage;
  transcript: APITranscript;
  version: number;
  transcriptId: string;
  onEditOperationError: (error: Error, action: TranscriptEditAction) => void;
  transcriptSelection: TranscriptSelection | null;
  updateTranscriptSelection: UpdateTranscriptSelectionType;
  scrollTranscriptToTime: (timeMs: number) => Promise<void>;
  undo: () => void;
  redo: () => void;
  backgroundAlignerStatus: AsyncStatus;
  syncScrollToTime: boolean;
  setSyncScrollToTime: React.Dispatch<React.SetStateAction<boolean>>;
  scrollTranscriptToCurrentTime: () => void;
  seekMedia: (options: {
    timeSeconds: number;
    play?: boolean;
    scroll?: boolean;
  }) => void;
  currentTime: number;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
  hasMedia: boolean;
}

export function Toolbar(props: ToolbarProps) {
  const {
    stage,
    metadata,
    transcript,
    version,
    transcriptId,
    onEditOperationError,
    transcriptSelection,
    updateTranscriptSelection,
    scrollTranscriptToTime,
    undo,
    redo,
    backgroundAlignerStatus,
    syncScrollToTime,
    setSyncScrollToTime,
    seekMedia,
    currentTime,
    setPaused,
    hasMedia,
  } = props;
  const { transcriptPermissions } = use(TranscriptPermissionsContext);

  if (!metadata) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton height={20} width={200} />
        <Skeleton height={35} width="100%" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-3 overflow-hidden">
        <LastSavedStatus />
        <span
          className={clsx(
            "inline-flex gap-2 overflow-hidden text-xs text-ellipsis whitespace-nowrap text-brand-700 opacity-0 transition-all",
            backgroundAlignerStatus === "pending" && "opacity-100",
          )}
        >
          <Icon icon={iconRefreshCcw} size="small" /> Aligning timestamps…
        </span>
      </div>

      {stage === "POST_TRANSCRIPTION" && (
        <div className="flex h-12 items-center overflow-hidden rounded-lg border border-gray-300 px-2 whitespace-nowrap">
          <div className="mr-2">
            <Tooltip content="Undo" position="bottom">
              <Button
                aria-label="Undo"
                icon={iconUndo2}
                disabled={!transcriptPermissions.edit}
                minimal
                intent="primary"
                onClick={undo}
              />
            </Tooltip>
            <Tooltip content="Redo" position="bottom">
              <Button
                aria-label="Redo"
                icon={iconRedo2}
                disabled={!transcriptPermissions.edit}
                minimal
                intent="primary"
                onClick={redo}
              />
            </Tooltip>
          </div>
          <span className="mx-1 h-4 w-px bg-gray-300" />
          {"hid" in navigator && navigator.hid && (
            <FootPedalButton
              seekMedia={seekMedia}
              currentTime={currentTime}
              setPaused={setPaused}
              hasMedia={hasMedia}
            />
          )}

          <Tooltip content="Keep current time in view" position="bottom">
            {/* TODO: switch to @kensho/neo toggle button when available https://github.com/kensho/neo/issues/118 */}
            <Button
              aria-label="Keep current time in view"
              icon={iconListEnd}
              minimal
              intent={syncScrollToTime ? "primary" : "default"}
              onClick={() => setSyncScrollToTime(!syncScrollToTime)}
            />
          </Tooltip>
          <Tooltip content="Copy to clipboard" position="bottom">
            <CopyToClipboardButton />
          </Tooltip>
          <ReviewButton
            onEditOperationError={onEditOperationError}
            updateTranscriptSelection={updateTranscriptSelection}
            scrollTranscriptToTime={scrollTranscriptToTime}
          />
          <ImportTranscriptButton
            onEditOperationError={onEditOperationError}
            transcriptSelection={transcriptSelection}
            transcript={transcript}
            version={version}
            transcriptId={transcriptId}
          />
          <span className="mx-1 h-4 w-px bg-gray-300" />
          <AlignSelectedButton
            transcriptId={transcriptId}
            transcriptSelection={transcriptSelection}
            updateTranscriptSelection={updateTranscriptSelection}
          />
          <CreateNeedsReviewHighlightButton
            transcriptId={transcriptId}
            transcriptSelection={transcriptSelection}
          />
        </div>
      )}
    </div>
  );
}
